import React, { useCallback } from "react";

import { Run, VcsProvider } from "types/generated";
import { Commit } from "components/icons/generated";
import Counter from "ds/components/Counter";
import MetaInfoListItem from "components/MetaInfoList/Item";
import useTypedContext from "hooks/useTypedContext";
import { StackContext } from "views/Stack/Context";
import Box from "ds/components/Box";
import { useToggle } from "hooks/useToggle";
import { getCommitDigitsCount } from "utils/commit";
import Typography from "ds/components/Typography";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import CardTitle from "ds/components/Card/components/Title";
import CardCollapsible from "ds/components/Card/Collapsible";

import CommitAuthorLink from "./CommitAuthorLink";
import styles from "./styles.module.css";

type RunsGroupProps = {
  run: Run;
  provider: VcsProvider;
  count: number;
  isOpen?: boolean;
  children?: React.ReactNode;
  someRunsShouldBeApproved: boolean;
};

const RunsGroup = (props: RunsGroupProps) => {
  const { run, provider, children, count, isOpen = false, someRunsShouldBeApproved } = props;
  const [isCollapsed, toggle] = useToggle(!isOpen);

  const { stack } = useTypedContext(StackContext);

  const handleToggle = useCallback(() => {
    toggle();
  }, [toggle]);

  return (
    <CardCollapsible
      headerAlign="start"
      ariaLevel={3}
      ariaLabel={run.title}
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      fullbleed
      title={
        <Box direction="column" gap="medium" fullWidth>
          <Box justify="between">
            <Box align="center">
              <CardTitle tag="span">{run.title}</CardTitle>
              <Box padding="0 medium">
                <Counter count={count} />
              </Box>
            </Box>
            {someRunsShouldBeApproved && (
              <Typography tag="span" variant="p-body2" color="warning">
                Needs approval
              </Typography>
            )}
          </Box>

          <Box gap="large" className={styles.meta}>
            <TimestampMetaInfo
              prefix="Committed on"
              timestamp={run.commit.timestamp}
              postfix={
                <>
                  by&nbsp;
                  <CommitAuthorLink
                    login={run.commit.authorLogin || undefined}
                    name={run.commit.authorName}
                    provider={provider}
                  />
                </>
              }
            />
            <MetaInfoListItem
              href={run.commit.url}
              linkText={run.commit.hash.slice(0, getCommitDigitsCount(provider))}
              icon={Commit}
              target="_blank"
              tooltip={stack.trackedCommit?.hash === run.commit.hash ? "Tracked commit" : null}
            />
          </Box>
        </Box>
      }
    >
      <div className={styles.wrapper}>{children}</div>
    </CardCollapsible>
  );
};

export default RunsGroup;
